import { Flex } from '@adobe/react-spectrum';
import { useNavigate, useLocation, useNavigationType, Outlet } from 'react-router-dom';
import { HeaderBar, PageBanner } from './components/headerBar/HeaderBar';
import { FooterBar } from './components/footerBar/FooterBar';
import { handleUpdatedAppRoute } from './data/redux/slices/appSlice';
import { getAnalytics, logEvent } from 'firebase/analytics';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { parseHash } from './utils/deepLink/useDeepLinkSegment';
import { useNitroPayNavigation } from './ads/useNitroPayNavigation';
import { useFocusOnNavigate } from './hooks/useFocusOnNavigate';
import { useScrollResetOnNavigate } from './hooks/useScrollResetOnNavigate';

export default function AppBody() {
    const navigate = useNavigate();
    const location = useLocation();
    const route = useSelector((state) => state.app.route);
    const dispatch = useDispatch();
    const userProfileName = useSelector((state) => state.userDetails?.profile?.metadata?.profileName);

    const prevRoute = useRef(route?.path || '/');
    const mainRef = useRef(null);

    useFocusOnNavigate(mainRef, location.pathname);

    const navigationType = useNavigationType();
    useScrollResetOnNavigate({ pathname: location.pathname, hash: location.hash, navigationType });

    useNitroPayNavigation();

    // Fire once per browser session if the user truly arrived on a details
    // page from off-domain. Referrer guard suppresses same-origin reloads /
    // bookmark revisits; sessionStorage guard is scoped per kind so a user
    // who later starts a new session that enters via a different page type
    // still registers that entry.
    useEffect(() => {
        try {
            const path = window.location.pathname;
            let kind = null;
            if (/^\/details\/deck\//.test(path)) kind = 'DECK';
            else if (/^\/commanders\/id\//.test(path)) kind = 'COMMANDER';
            else if (/^\/user\/id\//.test(path)) kind = 'USER';
            else if (/^\/authors\/id\//.test(path)) kind = 'AUTHOR';
            if (!kind) return;

            const storageKey = `entry_via_${kind}_fired`;
            if (sessionStorage.getItem(storageKey)) return;
            const referrer = document.referrer || '';
            if (referrer && referrer.startsWith(window.location.origin)) return;

            logEvent(getAnalytics(), `entry_via_${kind}`, { entry_page: path });
            sessionStorage.setItem(storageKey, '1');
        } catch (e) {
            // analytics not initialized yet, or sessionStorage blocked
        }
    }, []);

    // Fire once per browser session if the user truly arrived from off-domain
    // with a deep-link hash. Guards against the two inflation paths the old
    // version hit: (1) refresh after the user clicked any tab (tab state lives
    // in the hash), (2) bookmark/reload of a page they were already on.
    useEffect(() => {
        if (!window.location.hash) return;
        try {
            if (sessionStorage.getItem('entry_via_deep_link_fired')) return;
            const referrer = document.referrer || '';
            if (referrer && referrer.startsWith(window.location.origin)) return;

            const { segments, params } = parseHash(window.location.hash);
            const raw = window.location.hash.replace(/^#/, '');
            const clip = (v) => String(v).slice(0, 100);
            const payload = {
                entry_page: window.location.pathname,
                deep_link: clip(raw),
                deep_link_path: clip(segments.join('/')),
                deep_link_param_keys: clip(Object.keys(params).sort().join(',')),
            };
            for (const [k, v] of Object.entries(params)) {
                const key = `dl_${k}`.slice(0, 40);
                payload[key] = clip(v);
            }
            logEvent(getAnalytics(), 'entry_via_deep_link', payload);
            sessionStorage.setItem('entry_via_deep_link_fired', '1');
        } catch (e) {
            // analytics not initialized yet, or sessionStorage blocked
        }
    }, []);

    // Manual page_view logging (automatic tracking is disabled in Firebase config).
    // Wait for react-helmet to settle document.title before firing so GA4 records
    // a meaningful page_title instead of a "Loading..." placeholder, but always
    // fire within 500ms so slow/errored title swaps don't drop the pageview.
    useEffect(() => {
        let fired = false;
        const fire = () => {
            if (fired) return;
            fired = true;
            try {
                logEvent(getAnalytics(), 'page_view', {
                    page_title: document.title,
                    page_location: window.location.href,
                    page_path: location.pathname,
                });
            } catch (e) {
                // analytics not initialized yet
            }
        };

        const titleEl = document.querySelector('title');
        const observer =
            titleEl && typeof MutationObserver !== 'undefined'
                ? new MutationObserver(() => {
                      if (!document.title.includes('Loading...')) fire();
                  })
                : null;
        observer?.observe(titleEl, { childList: true });

        const fallback = setTimeout(fire, 500);

        if (!document.title.includes('Loading...')) fire();

        return () => {
            clearTimeout(fallback);
            observer?.disconnect();
        };
    }, [location.pathname]);

    if (
        /\/user\/id\/[0-9a-z]{8}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{12}/.test(window.location.href) &&
        userProfileName &&
        userProfileName !== ''
    ) {
        window.history.replaceState({}, null, `/user/id/${userProfileName}`);
    }

    useEffect(() => {
        if (!window.location.href.includes('?code=')) {
            let currentRoute = route?.path;

            if (prevRoute.current !== currentRoute) {
                const uri = window.location.pathname;
                let adoptingUrlAsRoute = false;

                if (!currentRoute && uri !== prevRoute.current) {
                    currentRoute = uri;
                    adoptingUrlAsRoute = true;
                }

                prevRoute.current = currentRoute || '/';

                navigate(currentRoute + (adoptingUrlAsRoute ? window.location.search + window.location.hash : ''));
                dispatch(handleUpdatedAppRoute({ route: currentRoute }));
            }
        }
    }, [route]);

    useEffect(() => {
        const onPopState = () => {
            if (window.location.href !== route?.path && !window.location.href.includes('?code=')) {
                const uri = window.location.pathname;

                if (prevRoute.current !== uri) {
                    prevRoute.current = uri;
                    dispatch(handleUpdatedAppRoute({ route: uri }));
                }
            }
        };

        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [route, dispatch]);

    return (
        <Flex id="AppContainer" direction="column">
            {/* Focus is moved by hand: a real fragment jump would write
                `#main-content` into the URL, and the report pages keep their
                open section and filters in the hash. */}
            <a
                href="#main-content"
                className="skipLink"
                onClick={(e) => {
                    e.preventDefault();
                    mainRef.current?.focus();
                }}
            >
                Skip to content
            </a>
            <HeaderBar />
            <main id="main-content" ref={mainRef} tabIndex={-1} className="appMain">
                <PageBanner />
                <Outlet />
            </main>
            <FooterBar />
        </Flex>
    );
}
